import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type { ChartPin } from '../api/types'
import { useAuth } from './auth'

/** What a chart needs to show and make pins (TimeSeriesChart `pinning`). */
export interface Pinning {
  pins: ChartPin[]
  error?: string
  create: (target: { parameter: string; ts: string; ts_end?: string }, note: string) => Promise<void>
  remove: (pin: ChartPin) => Promise<void>
  canRemove: (pin: ChartPin) => boolean
}

/** A station's pins for the time window and the charted parameters.
 *
 *  Loaded for the window, never the zoom, so a pin outside the zoomed view
 *  is still on the chart after Reset zoom. A pin made is added and a pin
 *  removed is dropped in place, rather than reloading the list, so the
 *  chart does not flicker; a new window or a new set of parameters asks
 *  again. A person removes their own pins, an admin anyone's (the server
 *  decides; this only hides the button). */
export function useChartPins(code: string | undefined, from: Date, to: Date,
                             parameters: string[], valid = true): Pinning {
  const { user } = useAuth()
  const [pins, setPins] = useState<ChartPin[]>([])
  const [error, setError] = useState<string>()
  const generation = useRef(0)
  const wanted = parameters.join(',')
  const start = from.getTime()
  const end = to.getTime()

  useEffect(() => {
    const mine = ++generation.current
    setPins([])
    setError(undefined)
    if (!code || !valid || !wanted) return
    api.pins.list(code, new Date(start), new Date(end), wanted.split(',')).then(
      (listed) => { if (mine === generation.current) setPins(listed) },
      (failed: unknown) => {
        if (mine === generation.current) {
          setError(failed instanceof Error ? failed.message : 'Could not load the pins.')
        }
      })
  }, [code, start, end, wanted, valid])

  const create = useCallback(async (target: { parameter: string; ts: string; ts_end?: string },
                                    note: string) => {
    if (!code) return
    const made = await api.pins.create(code, { ...target, note: note.trim() })
    setPins((current) => [...current, made])
  }, [code])

  const remove = useCallback(async (pin: ChartPin) => {
    await api.pins.remove(pin.id)
    setPins((current) => current.filter((p) => p.id !== pin.id))
  }, [])

  const isAdmin = user?.role === 'admin'
  const canRemove = useCallback((pin: ChartPin) => pin.mine || isAdmin, [isAdmin])

  return { pins, error, create, remove, canRemove }
}
